/* global BigInt */
export const AD_CAMPAIGNS_COLLECTION = 'adCampaigns';
export const AD_PLACEMENTS_COLLECTION = 'adPlacements';
export const AD_EVENTS_COLLECTION = 'adEvents';
export const AD_RENDERABLE_PLACEMENT_STATUSES = ['approved', 'active'];
export const AD_BLOCKING_PLACEMENT_STATUSES = ['pending', 'approved', 'active', 'paused'];
export const AD_MUTABLE_PLACEMENT_STATUSES = ['pending', 'approved', 'active', 'paused', 'rejected', 'ended'];

export const AD_MEDIA_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
    { value: 'link', label: 'Link' },
];

export const AD_MEDIA_LIMITS_BYTES = {
    text: 0,
    link: 50 * 1024 * 1024,
    image: 50 * 1024 * 1024,
    audio: 50 * 1024 * 1024,
    video: 100 * 1024 * 1024,
};

export const AD_APPROVAL_MODES = [
    { value: 'approvalRequired', label: 'Require Approval' },
    { value: 'open', label: 'Open to Eligible Creators' },
    { value: 'inviteOnly', label: 'Invite Only' },
];

export const DEFAULT_AD_ALLOWED_MEDIA_TYPES = AD_MEDIA_TYPES.map((type) => type.value);

export const normalizeAdMedium = (value) => {
    const normalized = String(value || '').trim();
    return AD_MEDIA_TYPES.some((type) => type.value === normalized) ? normalized : 'text';
};

export const getAdMediaLimitBytes = (medium) => (
    AD_MEDIA_LIMITS_BYTES[normalizeAdMedium(medium)] || 0
);

export const formatAdMediaLimit = (medium) => {
    const bytes = getAdMediaLimitBytes(medium);
    if (!bytes) return 'No file upload';
    return `${Math.round(bytes / 1024 / 1024)} MB`;
};

export const normalizeAllowedAdMediaTypes = (values) => {
    const selected = Array.isArray(values) ? values : DEFAULT_AD_ALLOWED_MEDIA_TYPES;
    const allowed = selected
        .filter((value) => AD_MEDIA_TYPES.some((type) => type.value === value))
        .filter((value, index, arr) => arr.indexOf(value) === index);
    return allowed;
};

export const isAdMediumAllowed = (medium, allowedMediaTypes) => (
    normalizeAllowedAdMediaTypes(allowedMediaTypes).includes(normalizeAdMedium(medium))
);

export const isRenderableAdPlacementStatus = (status) => (
    AD_RENDERABLE_PLACEMENT_STATUSES.includes(String(status || ''))
);

export const isBlockingAdPlacementStatus = (status) => (
    AD_BLOCKING_PLACEMENT_STATUSES.includes(String(status || ''))
);

export const buildAdCreativeUploadPath = ({ medium, uid, fileName }) => {
    const safeUid = String(uid || 'anonymous');
    const safeName = String(fileName || 'creative').replace(/[^\w.-]/g, '_');
    const stamp = Date.now();
    switch (normalizeAdMedium(medium)) {
        case 'video':
            return `videos/${safeUid}/ads/${stamp}_${safeName}`;
        case 'audio':
            return `audio/${safeUid}/ads/${stamp}_${safeName}`;
        case 'link':
            return `linkImages/${safeUid}/ads/${stamp}_${safeName}`;
        case 'image':
        default:
            return `images/${safeUid}/ads/${stamp}_${safeName}`;
    }
};

export const normalizeAdCreative = (campaign = {}) => {
    const medium = normalizeAdMedium(campaign.medium || campaign.creative?.medium);
    const creative = campaign.creative || {};
    return {
        medium,
        title: String(creative.title || campaign.campaignName || '').trim(),
        body: String(creative.body || campaign.description || '').trim(),
        url: sanitizeAdUrl(creative.url),
        assetUrl: sanitizeAdUrl(creative.assetUrl),
        imageUrl: sanitizeAdUrl(creative.imageUrl),
        destinationUrl: sanitizeAdUrl(campaign.destinationUrl || creative.destinationUrl),
    };
};

export function sanitizeAdUrl(value) {
    try {
        const url = new URL(String(value || '').trim());
        return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : '';
    } catch { return ''; }
}
export function formatAdAmount(value, currency = 'SOL') {
    if (!/^\d+$/.test(String(value))) return '0';
    const decimals = currency === 'SOL' ? 9 : 6;
    const digits = BigInt(value).toString().padStart(decimals + 1, '0');
    const fraction = digits.slice(-decimals).replace(/0+$/, '');
    return digits.slice(0, -decimals) + (fraction ? '.' + fraction : '');
}
export function isAdCampaignServing(campaign, now = Date.now(), placement = null) {
    try {
        return campaign?.schemaVersion === 2 && campaign.status === 'active' && campaign.escrowStatus === 'funded' &&
            /^[a-f0-9]{64}$/.test(campaign.termsHash || '') &&
            Number.isSafeInteger(campaign.escrowVerifiedAtMs) && now >= campaign.escrowVerifiedAtMs && now - campaign.escrowVerifiedAtMs < 120000 &&
            BigInt(campaign.remainingBudgetAtomic || '0') > 0n && now >= campaign.startsAtMs && now < campaign.endsAtMs &&
            (campaign.dailyCapAtomic === '0' || campaign.spendDay !== Math.floor(now / 86400000) || BigInt(campaign.dailySpentAtomic || '0') < BigInt(campaign.dailyCapAtomic)) &&
            (!placement || (placement.termsHash === campaign.termsHash && (campaign.perCreatorCapAtomic === '0' || BigInt(placement.allocatedAtomic || '0') < BigInt(campaign.perCreatorCapAtomic))));
    } catch { return false; }
}
export function resolveAdPanel(campaign, panels, placements, selectedId) {
    const compatible = panels.filter(panel => isAdMediumAllowed(campaign.medium, panel.allowedMediaTypes));
    const available = panel => !placements.some(placement => String(placement.componentId) === String(panel.id) && isBlockingAdPlacementStatus(placement.status) && String(placement.campaignId) !== String(campaign.id));
    return selectedId ? compatible.find(panel => String(panel.id) === String(selectedId)) : compatible.find(available) || compatible[0] || null;
}
